import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex } from 'design-system-web';
import cx from 'classnames';
import { useMutation } from '@apollo/client';
import { useCountdownTimer } from 'react-hooks';
import { useFlag } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';
import getErrorCodes from 'lane-shared/helpers/getErrorCodes';
import {
  parsePhoneNumber,
} from 'libphonenumber-js';
import { errorCodes } from 'activate-errors';

import {
  requestPhoneNumberVerification,
  verifyPhoneNumber,
} from 'lane-shared/graphql/user';
import { CodeField } from '../CodeField/CodeField';
import { Alert } from 'components/lds';

import styles from './index.scss';

const maskPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return '';
  
  try {
    // Parse the phone number to extract country code
    const parsed = parsePhoneNumber(phoneNumber);
    
    if (!parsed) {
      return phoneNumber;
    }

    const countryCallingCode = parsed.countryCallingCode;
    const nationalNumber = parsed.nationalNumber;

    if (nationalNumber.length <= 4) {
      // If national number is too short, show country code and asterisks
      return `+${countryCallingCode}${'*'.repeat(nationalNumber.length)}`;
    }

    // Show full country code, mask middle digits, show last 4 digits
    const lastFour = nationalNumber.slice(-4);
    const maskedMiddle = '*'.repeat(nationalNumber.length - 4);

    return `+${countryCallingCode} ${maskedMiddle}${lastFour}`;
  } catch {
    // If parsing fails (invalid phone number format), return unmasked phone number
    return phoneNumber;
  }
};

type Props = {
  phoneNumber: string;
  userLoginId: string;
  onBack: () => void;
  onSuccess: () => void;
};

const formatCountdown = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;

  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const MAX_RESEND_ATTEMPTS = 2;
const RESEND_COOLDOWN_SECONDS = 30;
const LOCKOUT_DURATION_SECONDS = 300;

export const OTPVerifyForm = ({
  phoneNumber,
  userLoginId,
  onBack,
  onSuccess,
}: Props) => {
  const { t } = useTranslation();
  const isSavePhoneVerificationEnabled = useFlag(
    FeatureFlag.SavePhoneVerificationDuringOnboarding,
    false
  );

  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [incorrectAttempts, setIncorrectAttempts] = useState(0);
  const [resendAttempts, setResendAttempts] = useState(0);
  const [unknownError, setUnknownError] = useState<string | null>(null);
  const [hasSendError, setHasSendError] = useState(false);

  const resendTimerDuration = useRef(RESEND_COOLDOWN_SECONDS);

  const maskedPhoneNumber = maskPhoneNumber(phoneNumber);

  const [requestVerification] = useMutation(requestPhoneNumberVerification);
  const [verifyPhone] = useMutation(verifyPhoneNumber);

  // Countdown timer for resend button
  const { countdown: resendCountdown, start: startResendTimer, isRunning: isResendTimerRunning } = useCountdownTimer(30, {
    autoStart: false,
  });

  // Countdown timer for 5-minute lockout after max attempts
  const {  start: startLockoutTimer, isRunning: isLockoutTimerRunning } = useCountdownTimer(300, {
    autoStart: false,
  });

  const MAX_ATTEMPTS = 5;
  const isMaxAttemptsReached = incorrectAttempts >= MAX_ATTEMPTS;
  const isLockedOut = isMaxAttemptsReached && isLockoutTimerRunning;
  const isVerificationCodeValid = verificationCode.length === 6;
  const isVerifyDisabled = !isVerificationCodeValid || isLoading || isLockedOut || hasSendError;

  const handleError = useCallback((error: unknown) => {
    const errorCode = getErrorCodes(error)[0];
    
    if (errorCode === errorCodes.phoneVerificationCodeIncorrectError.code) {
      // Increment incorrect attempts counter and check in the same update
      setIncorrectAttempts(prev => {
        const newAttempts = prev + 1;
        
        if (newAttempts >= MAX_ATTEMPTS) {
          setError(t('web.onboarding.userProfile.phone.otp.tooManyAttemptsError'));
          // Start 5-minute lockout timer when max attempts is reached
          startLockoutTimer();

          // Reset OTP code
          setVerificationCode('');
        } else {
          setError(t('web.onboarding.userProfile.phone.otp.incorrectCodeError', { attemptsRemaining: MAX_ATTEMPTS - newAttempts }));
        }
        
        return newAttempts;
      });
    } else if (errorCode === errorCodes.phoneVerificationError.code) {
      setError(
        t('web.onboarding.userProfile.phone.otp.send.error')
      );
      setHasSendError(true);
    } else {
      setUnknownError(t('web.onboarding.userProfile.unknownError.message'));
    }
  }, [t, startLockoutTimer]);

  const sendVerificationSMS = useCallback(async () => {
    // setIsLoading(true);
    setError(null);
    setHasSendError(false);
    startResendTimer(resendTimerDuration.current);

    try {
      const { data } = await requestVerification({
        variables: {
          userLoginId,
          phoneNumber,
        },
      });

      if (!data?.requestPhoneNumberVerification) {
        setError(
          t('web.onboarding.userProfile.phone.otp.send.error')
        );
        setHasSendError(true);
      } else {
        // Reset incorrect attempts counter when new SMS is sent
        setIncorrectAttempts(0);
        setHasSendError(false);
      }
    } catch (error: unknown) {
        handleError(error);
    } finally {
      setIsLoading(false);
    }
  }, [handleError, phoneNumber, requestVerification, startResendTimer, t, userLoginId]);

  // Send verification SMS when component mounts
  useEffect(() => {
    if (userLoginId && phoneNumber) {
      sendVerificationSMS();
    }
  }, [userLoginId, phoneNumber, sendVerificationSMS]);

  // Reset incorrect attempts when lockout timer expires
  useEffect(() => {
    if (!isLockoutTimerRunning && isMaxAttemptsReached) {
      setIncorrectAttempts(0);
      setError(null);

      if (isSavePhoneVerificationEnabled) {
        setResendAttempts(0);
        resendTimerDuration.current = RESEND_COOLDOWN_SECONDS;
      }
    }
  }, [isLockoutTimerRunning, isMaxAttemptsReached, isSavePhoneVerificationEnabled]);

  // Reset resend count after 5-min timer expires so user gets a fresh cycle
  useEffect(() => {
    if (isSavePhoneVerificationEnabled && !isResendTimerRunning && resendAttempts >= MAX_RESEND_ATTEMPTS) {
      setResendAttempts(0);
      resendTimerDuration.current = RESEND_COOLDOWN_SECONDS;
    }
  }, [isResendTimerRunning, resendAttempts, isSavePhoneVerificationEnabled]);

  const handleVerifyCode = async () => {
    if (!isVerificationCodeValid) {
      setError(t('Please enter a valid 6-digit verification code'));

      return;
    }

    // Don't allow verification if disabled
    if (isVerifyDisabled) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data } = await verifyPhone({
        variables: {
          userLoginId,
          phoneNumber,
          code: verificationCode,
        },
      });

      if (data?.verifyPhoneNumber) {
        onSuccess();
      } else {
        const error = (data?.errors && data?.errors[0]) || {graphQLErrors: [{
          extensions: {
            exception: {
              code: errorCodes.phoneVerificationCodeIncorrectError.code,
            },
          },
        }]};

        handleError(error);
      }
    } catch (error: unknown) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = () => {
    if (isResendTimerRunning || isLockedOut) {
      return; // Don't allow resend during countdown or lockout
    }

    if (isSavePhoneVerificationEnabled) {
      const nextResendCount = resendAttempts + 1;

      setResendAttempts(nextResendCount);

      if (nextResendCount >= MAX_RESEND_ATTEMPTS) {
        resendTimerDuration.current = LOCKOUT_DURATION_SECONDS;
      }
    }
    
    setVerificationCode('');
    setError(null);
    sendVerificationSMS();
  };

  const resendButtonLabel = useMemo(() => {
    if (!isResendTimerRunning) {
      return t('Resend OTP');
    }

    if (isSavePhoneVerificationEnabled) {
      return t('web.onboarding.userProfile.phone.otp.resend.timerFormatted', {
        countdown: formatCountdown(resendCountdown),
      });
    }

    return t('web.onboarding.userProfile.phone.otp.resend.timer', {
      countdown: resendCountdown,
    });
  }, [isResendTimerRunning, isSavePhoneVerificationEnabled, resendCountdown, t]);

  return (
    <div className={styles.verificationScreen}>
      <div className={styles.content}>
        <div className={styles.instructions}>
          <p className={styles.mainText}>{t('Enter the 6-digit code')}</p>
          {unknownError && <Flex direction="row" className={styles.alertContainer}>
          <Alert type="error" className={styles.alert}>
            {unknownError}
          </Alert>
        </Flex>
         }
          <p className={styles.phoneText}>
            {t('We sent a code to {{phoneNumber}}', {
              phoneNumber: maskedPhoneNumber,
            })}
          </p>
        </div>

        <div className={styles.codeInput}>
          <CodeField
            value={verificationCode}
            onChange={setVerificationCode}
            cellCount={6}
            disabled={isLoading || isLockedOut || hasSendError}
          />
          {error && <div className={styles.errorMessage}>{error}</div>}
        </div>
        <div className={styles.submitButton}>
          <Button
            variant="primary"
            onClick={handleVerifyCode}
            disabled={isVerifyDisabled}
            size="large"
            fullWidth
          >
            {isLoading ? t('Verifying') : t('Verify')}
          </Button>
        </div>
      </div>
      <div className={styles.footer}>
        <button
          className={cx(styles.resendLink, (isResendTimerRunning || isLockedOut) && styles.disabled)}
          onClick={handleResendCode}
          disabled={isLoading || isResendTimerRunning || isLockedOut}
          type="button"
        >
          {resendButtonLabel}
        </button>

        <button
          className={styles.resendLink}
          onClick={onBack}
          disabled={isLoading}
          type="button"
        >
          {t('Change Mobile Number')}
        </button>
      </div>
    </div>
  );
};
